import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import subscriptionService from '../../services/subscription-service';
import productService from '../../db/product-service';
import paymentService from '../../db/payment-service';
import customerService from '../../db/customer-service';
import bottleReturnService from '../../db/bottle-return-service';
import './SubscriptionCheckout.scss';

const SubscriptionCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { planType, selectedProducts, deliverySchedule } = location.state || {};
  const [plan, setPlan] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressLocked, setAddressLocked] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState('pickup');
  const [customerCredits, setCustomerCredits] = useState(null);
  const [appliedCredits, setAppliedCredits] = useState(0);
  const [useCredits, setUseCredits] = useState(false);
  const customerId = sessionStorage.getItem('customerId');
  const isAuthenticated = sessionStorage.getItem('isUserAuthenticated') === 'true';
  const customerEmail = sessionStorage.getItem('customerEmail');

  useEffect(() => {
    if (!isAuthenticated || !customerId) {
      navigate('/login');
      return;
    }
    if (!planType || !selectedProducts) {
      navigate('/subscriptions');
      return;
    }
    loadData();
    loadAddresses();
    loadCustomerCredits();
  }, [planType, selectedProducts, isAuthenticated, customerId, customerEmail, navigate]);

  const loadCustomerCredits = async () => {
    try {
      const credits = await bottleReturnService.getCustomerCredits(customerId);
      setCustomerCredits(credits);
    } catch (error) {
      console.error('Error loading customer credits:', error);
    }
  };

  const availableCredits = customerCredits ? (customerCredits.totalCredits - customerCredits.usedCredits) : 0;

  const handleApplyCredits = (amount) => {
    if (amount > availableCredits) {
      alert('Cannot apply more credits than available');
      return;
    }
    setAppliedCredits(amount);
  };

  const loadAddresses = async () => {
    try {
      const customerAddresses = await customerService.getCustomerAddresses(customerId);
      setAddresses(customerAddresses);
      const defaultAddress = customerAddresses.find(addr => addr.isDefault);
      if (defaultAddress && !selectedAddress) {
        setSelectedAddress(defaultAddress);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
    }
  };

  const loadData = async () => {
    try {
      const [plansData, productsData] = await Promise.all([
        subscriptionService.getSubscriptionPlans(),
        productService.getAllProducts()
      ]);
      
      const currentPlan = plansData.find(p => p.planType === planType);
      setPlan(currentPlan);
      
      const selectedProductDetails = selectedProducts.map(sp => {
        const product = productsData.find(p => p._id === sp.productId);
        return { ...product, quantity: sp.quantity };
      });
      setProducts(selectedProductDetails);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const calculateSubscriptionDates = () => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 1);
    while (startDate.getDay() === 0 || startDate.getDay() === 6) {
      startDate.setDate(startDate.getDate() + 1);
    }
    
    let endDate = new Date(startDate);
    let workingDays = 1;
    while (workingDays < 20) {
      endDate.setDate(endDate.getDate() + 1);
      const dayOfWeek = endDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
    }
    
    return {
      startDate: startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
      endDate: endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    };
  };

  const handleSubscribe = async () => {
    const isTrial = planType === 'free_trial';

    if (!isTrial && deliveryOption === 'delivery' && !selectedAddress) {
      alert('Please select a delivery address.');
      return;
    }

    setAddressLocked(true);
    setProcessing(true);
    try {
      // Build customer info directly from session/localStorage — avoids direct CouchDB call
      // which fails on mobile due to CORS restrictions on the direct DB URL
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const customerInfo = {
        customerId,
        name: storedUser.name || sessionStorage.getItem('customerName') || 'Customer',
        email: storedUser.email || sessionStorage.getItem('customerEmail') || '',
        phone: storedUser.phone || sessionStorage.getItem('customerPhone') || ''
      };

      if (isTrial) {
        // Set optimistic flag immediately so SubscriptionPlans hides the card on return
        sessionStorage.setItem('trialJustClaimed', 'true');

        // Store delivery schedule for PaymentSuccessHandler handoff
        // Use localStorage — survives Razorpay cross-origin redirect (sessionStorage does not)
        // Keyed by customerId to prevent stale schedule from a different user on same device
        localStorage.setItem('trialDeliverySchedule', JSON.stringify({ customerId, schedule: deliverySchedule }));

        const subscriptionData = {
          subscriptionId: `trial_${customerId}`,
          planName: plan.name,
          planType: 'free_trial',
          monthlyPrice: 55
        };
        await paymentService.processSubscriptionPayment(subscriptionData, customerInfo);
        // Razorpay redirects to /payment/success — PaymentSuccessHandler takes over
        return;
      }

      // Regular plan payment — store subscription data before Razorpay redirect
      // (window.location.href redirect wipes page context, so we use localStorage)
      localStorage.setItem('pendingSubscription', JSON.stringify({
        customerId,
        planType,
        selectedProducts,
        deliverySchedule,
        selectedAddress,
        deliveryOption,
        appliedCredits
      }));

      const subscriptionData = {
        subscriptionId: 'temp_' + Date.now(),
        planName: plan.name,
        planType,
        monthlyPrice: Math.max(0, plan.monthlyPrice + (deliveryOption === 'delivery' ? 500 : 0) - appliedCredits)
      };

      await paymentService.processSubscriptionPayment(subscriptionData, customerInfo);
      // Razorpay redirects to /payment/success — PaymentSuccessHandler takes over
    } catch (error) {
      console.error('Error creating subscription:', error);
      let errorMessage = 'Error processing subscription. Please try again.';
      if (error.error === 'Payment cancelled by user') {
        errorMessage = 'Payment was cancelled. No subscription was created.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert(errorMessage);
    }
    setProcessing(false);
  };

  if (!isAuthenticated || !customerId) {
    return <div className="loading">Redirecting to login...</div>;
  }

  if (loading) return <div className="loading">Loading checkout...</div>;
  if (!plan) return <div className="error">Plan not found</div>;

  const isTrial = planType === 'free_trial';

  return (
    <div className="subscription-checkout">
      <div className="checkout-header">
        <h1>Complete Your {isTrial ? 'Welcome Gift' : 'Subscription'}</h1>
        <div className="plan-info">
          <h2>{plan.name}</h2>
          <div className="price">{isTrial ? '₹100 activation' : `₹${plan.monthlyPrice}/month`}</div>
        </div>
      </div>

      <div className="checkout-content">
        <div className="selected-products">
          <h3>Your {isTrial ? '5-Day' : 'Monthly'} Selection</h3>
          <div className="products-list">
            {products.filter(p => p && p._id).map(product => (
              <div key={product._id} className="product-item">
                <div className="product-info">
                  <h4>{product.name}</h4>
                </div>
                <div className="quantity">
                  {isTrial ? `${product.quantity} shot${product.quantity !== 1 ? 's' : ''}` : `${product.quantity} per month`}
                </div>
              </div>
            ))}
          </div>
          {isTrial && (
            <div className="auto-selection-note">
              <p>📅 Deliveries scheduled Mon–Fri starting tomorrow. Weekends skipped.</p>
            </div>
          )}
        </div>

        {!isTrial && (
          <>
            <div className="delivery-options">
              <h3>Delivery Options</h3>
              <div className="delivery-choice">
                <label className="delivery-option">
                  <input type="radio" name="delivery" value="pickup"
                    checked={deliveryOption === 'pickup'}
                    onChange={e => setDeliveryOption(e.target.value)} />
                  <div className="option-content">
                    <div className="option-header">
                      <strong>Store Pickup</strong>
                      <span className="price">FREE</span>
                    </div>
                    <p>Pick up your subscription items from our store</p>
                    <div className="store-address">
                      <small>
                        <strong>Pickup Location:</strong><br />
                        Opp Candeur, Bapu Nagar<br />
                        Serilingampalle (M)<br />
                        Hyderabad, Telangana 500019<br />
                        Phone: +91 9390649371
                      </small>
                    </div>
                  </div>
                </label>
                <label className="delivery-option">
                  <input type="radio" name="delivery" value="delivery"
                    checked={deliveryOption === 'delivery'}
                    onChange={e => setDeliveryOption(e.target.value)} />
                  <div className="option-content">
                    <div className="option-header">
                      <strong>Home Delivery</strong>
                      <span className="price">₹500/month</span>
                    </div>
                    <p>Get your subscription delivered to your doorstep</p>
                  </div>
                </label>
              </div>
            </div>

            {deliveryOption === 'delivery' && (
              <div className="address-selection">
                <h3>Delivery Address</h3>
                {addresses.length > 0 ? (
                  <div className="address-options">
                    {addresses.map(address => (
                      <div
                        key={address.id}
                        className={`address-option ${selectedAddress?.id === address.id ? 'selected' : ''} ${addressLocked ? 'locked' : ''}`}
                        onClick={() => !addressLocked && setSelectedAddress(address)}
                      >
                        <div className="address-radio">
                          <input type="radio" name="address"
                            checked={selectedAddress?.id === address.id}
                            onChange={() => !addressLocked && setSelectedAddress(address)}
                            disabled={addressLocked} />
                        </div>
                        <div className="address-details">
                          <div className="address-header">
                            <strong>{address.fullName}</strong>
                            {address.isDefault && <span className="default-badge">Default</span>}
                          </div>
                          <div className="address-text">
                            {address.addressLine1}
                            {address.addressLine2 && <>, {address.addressLine2}</>}<br />
                            {address.city}, {address.state} - {address.pincode}<br />
                            Phone: {address.phone}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-addresses">
                    <p>No addresses found. Please add an address in your profile first.</p>
                    <button onClick={() => navigate('/profile')} className="add-address-btn">Add Address</button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div className="subscription-summary">
          <h3>Order Summary</h3>
          <div className="summary-item">
            <span>Plan:</span>
            <span>{plan.name}</span>
          </div>
          {isTrial ? (
            <>
              <div className="summary-item">
                <span>Delivery:</span>
                <span>FREE (Mon–Fri, 5 days)</span>
              </div>
              <div className="summary-item final-total">
                <span>Activation Fee:</span>
                <span className="total">₹100</span>
              </div>
            </>
          ) : (
            <>
              <div className="summary-item">
                <span>Monthly Plan Cost:</span>
                <span>₹{plan.monthlyPrice}</span>
              </div>
              <div className="summary-item">
                <span>Delivery Option:</span>
                <span>{deliveryOption === 'delivery' ? 'Home Delivery' : 'Store Pickup'}</span>
              </div>
              {deliveryOption === 'delivery' && (
                <div className="summary-item">
                  <span>Delivery Charge:</span>
                  <span>₹500</span>
                </div>
              )}
              {availableCredits > 0 && (
                <div className="credits-section">
                  <div className="summary-item">
                    <span>Available Credits:</span>
                    <span>₹{availableCredits}</span>
                  </div>
                  <label className="apply-credits-label">
                    <input type="checkbox" checked={useCredits}
                      onChange={e => { setUseCredits(e.target.checked); if (!e.target.checked) setAppliedCredits(0); }} />
                    Apply Credits
                  </label>
                  {useCredits && (
                    <div className="credits-input-group">
                      <input type="number" min="0" max={availableCredits} value={appliedCredits}
                        onChange={e => handleApplyCredits(parseInt(e.target.value) || 0)}
                        placeholder="Enter amount" />
                      <span className="max-credits">Max: ₹{availableCredits}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="summary-item total">
                <span>Subtotal:</span>
                <span className="total">₹{plan.monthlyPrice + (deliveryOption === 'delivery' ? 500 : 0)}</span>
              </div>
              {appliedCredits > 0 && (
                <div className="summary-item discount">
                  <span>Credits Applied:</span>
                  <span>-₹{appliedCredits}</span>
                </div>
              )}
              <div className="summary-item final-total">
                <span>Final Amount:</span>
                <span className="total">₹{Math.max(0, plan.monthlyPrice + (deliveryOption === 'delivery' ? 500 : 0) - appliedCredits)}</span>
              </div>
              {deliveryOption === 'delivery' ? (
                <div className="summary-item">
                  <span>Delivery Address:</span>
                  <span>{selectedAddress ? `${selectedAddress.fullName}, ${selectedAddress.city}` : 'Not selected'}</span>
                </div>
              ) : (
                <div className="summary-item">
                  <span>Pickup Location:</span>
                  <span>Opp Candeur, Bapu Nagar, Hyderabad</span>
                </div>
              )}
              <div className="summary-item">
                <span>Delivery Days:</span>
                <span>20 days/month (Mon-Fri)</span>
              </div>
              <div className="summary-item">
                <span>Start Date:</span>
                <span>{calculateSubscriptionDates().startDate}</span>
              </div>
              <div className="summary-item">
                <span>End Date:</span>
                <span>{calculateSubscriptionDates().endDate}</span>
              </div>
            </>
          )}
          <div className="summary-item">
            <span>Payment Method:</span>
            <span>Cards/UPI/Wallets</span>
          </div>
        </div>

        <div className="checkout-actions">
          <button className="back-btn" onClick={() => navigate(-1)}>Back</button>
          <button
            className="subscribe-btn"
            onClick={handleSubscribe}
            disabled={processing || (!isTrial && deliveryOption === 'delivery' && !selectedAddress)}
          >
            {processing ? 'Processing...' : isTrial ? 'Pay ₹100 & Activate' : 'Subscribe Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCheckout;
